import { BASE_URL, HTTP_STATUS } from "@api/constant";
import CASBlock from "@blocks/CASBlock/CASBlock";
import PromoteEpaper from "@blocks/PromoteEpaper/PromoteEpaper";
import { CinemaArticles } from "@components/Layouts/Home/CinemaArticles";
import { HoroscopeSection } from "@components/Layouts/Home/HoroscopeSection";
import { JobsListing } from "@components/Layouts/Home/JobsListing";
import { MovieTimingsSection } from "@components/Layouts/Home/MovieTimingsSection";
import { RecentArticlesSection } from "@components/Layouts/Home/RecentArticlesSection";
import { SectionArticles } from "@components/Layouts/Home/SectionArticles";
import TNEHomeWidget from "@components/Layouts/Home/TNEHomeWidget";
import { TopNewsSection } from "@components/Layouts/Home/TopNewsSection";
import { VideoPlaylistSection } from "@components/Layouts/Home/VideoPlaylistSection";
import MainBlock from "@container/MainBlock";
import MainContainer, { AdsSettings } from "@container/MainContainer";
import { GoldSection } from "@container/SideBlock/GoldSection";
import SideBlock from "@container/SideBlock/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import { AdsImu2 } from "@elements/Advertisement/variant/AdsImu";
import Divider from "@elements/Divider/Divider";
import {
  BC_PLAYER_ID_HOME,
  HOME_CAS_CAMPAIGN_ID,
} from "@helper/getEnvVariables";
import { GADataProps, getGTMData } from "@helper/getGtmData";
import useCasScript from "@hooks/useCasScript";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import useWindowDimensions from "@hooks/useWindowDimensions";
import { Route } from "@route-types/Home/+types/Home";
import type {
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import {
  createMetaTags,
  MetaTagsProps,
  USFWebSiteSchema,
} from "@sphtech/web2-seo-schema";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { HomePageDataPayload } from "@typings/PagePayloadType";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import { BudgetBook } from "../Keyword/Variants/BudgetVariant";
import fetchHomeData from "./Home.server";

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    HomePageDataPayload,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
}

export default function HomePage() {
  const routeContext: TRouteWithoutRedirect<HomePageDataPayload, string> =
    useLoaderData();

  const dataLoaderResponse = routeContext;
  const adSettings: AdsSettings = useGlobalAdSetting();
  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    throw new Error("Client Error");
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    throw new Error("Server Error");
  }

  const { isScriptReady: isCasScriptReady } = useCasScript();

  const pageAdTargetValue = PageAdTargetingTypeEnum.HOME;

  const sectionName = "/";
  const { width } = useWindowDimensions();
  const isMobile = width <= 480;

  const recentArticles = dataLoaderResponse.payload.recentArticles ?? [];
  const topNewsArticles = dataLoaderResponse.payload.topNewsArticles ?? [];
  const epaperCoverImageUrl =
    dataLoaderResponse.payload.epaperCoverImage?.coverImageUri || "";
  const sectionArticles = dataLoaderResponse.payload.sectionArticles ?? {};

  const bcVideos = dataLoaderResponse.payload.bcVideos;

  const goldPrices = dataLoaderResponse.payload.goldPrices;
  const forexPairs = dataLoaderResponse.payload.forexPairs;
  const movies = dataLoaderResponse.payload.movies;
  const jobs = dataLoaderResponse.payload.jobs;

  const mostRead = dataLoaderResponse.payload.mostRead;
  const sidebarSections = dataLoaderResponse.payload.sidebarSections;

  const tnePayload = dataLoaderResponse.payload.tnePayload;
  const budget2026PdfLink = dataLoaderResponse.payload.budget2026PdfLink;

  return (
    <>
      <USFWebSiteSchema
        name="TamilMurasu"
        url={BASE_URL}
        searchUrl={`${BASE_URL}/search/site/{search_term_string}`}
      />
      <MainContainer
        sectionName={sectionName}
        pageAdTargetValue={pageAdTargetValue}
        suppliedAdsSettings={adSettings}
        eventBanner={tnePayload ? false : true}
      >
        {tnePayload && <TNEHomeWidget {...tnePayload} />}
        <WrapperBlock className="flex flex-col lg:flex-row justify-between">
          <MainBlock showBreadcrumbs={false} showTitle={false}>
            <>
              <TopNewsSection topNewsArticles={topNewsArticles} />
              {budget2026PdfLink && (
                <BudgetBook
                  pdfLink={budget2026PdfLink}
                  tagName={"பட்ஜெட் 2026"}
                  className="lg:hidden"
                />
              )}
              {isMobile && (
                <>
                  <AdsImu2
                    sectionName="home"
                    pageAdTargetType={pageAdTargetValue}
                  />
                  <Divider className="border-b border-gray-2200" />
                  <PromoteEpaper
                    className="lg:hidden border-t-0 pt-0"
                    epaperCoverImageUrl={epaperCoverImageUrl}
                  />
                </>
              )}

              <RecentArticlesSection recentArticles={recentArticles} />
              {isCasScriptReady && HOME_CAS_CAMPAIGN_ID && (
                <CASBlock casId={HOME_CAS_CAMPAIGN_ID} />
              )}
              <VideoPlaylistSection
                playerId={BC_PLAYER_ID_HOME}
                bcVideos={bcVideos}
              />
              <SectionArticles {...sectionArticles} />
              {isMobile && goldPrices?.status == HTTP_STATUS.SUCCESS && (
                <>
                  {" "}
                  <GoldSection goldPrices={goldPrices} />
                  <Divider className="mt-5 border-b border-gray-2200" />
                </>
              )}
              {jobs?.status == HTTP_STATUS.SUCCESS && (
                <JobsListing jobs={jobs} />
              )}
              <HoroscopeSection />
              {movies?.status == HTTP_STATUS.SUCCESS && (
                <MovieTimingsSection movies={movies} />
              )}
              <CinemaArticles
                articlesPayload={sectionArticles.cinemaArticles}
              />
            </>
          </MainBlock>

          <SideBlock
            sectionName="home"
            hasTaboola={adSettings.isTaboolaEnabled}
            pageAdTargetValue={pageAdTargetValue}
            hasTranslate
            promoteEpaper={!isMobile}
            epaperCoverImageUrl={epaperCoverImageUrl}
            mostRead={mostRead}
            sidebarSections={sidebarSections}
            {...(forexPairs?.response == HTTP_STATUS.SUCCESS && { forexPairs })}
            {...(!isMobile &&
              goldPrices?.status == HTTP_STATUS.SUCCESS && { goldPrices })}
            {...(!isMobile &&
              budget2026PdfLink && { budget2026PdfLink: budget2026PdfLink })}
          />
        </WrapperBlock>
      </MainContainer>
    </>
  );
}

export async function loader() {
  const data = (await fetchHomeData()) as TRouteWithRedirect<
    HomePageDataPayload,
    string
  >;

  if (data.statusCode !== HTTP_STATUS.SUCCESS) return { ...data };
  const gaData = data.payload.gaData;

  return {
    ...data,
    gtm: getGTMData(gaData as GADataProps),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<HomePageDataPayload, string>
  >;
}
